import React, { useState, useEffect } from 'react';
import { SUBCATEGORIES } from '../../constants/subcategories';
import SizeEditor from './SizeEditor';
import ImageUploader from '../ImageUploader';
import {
  createProduct,
  updateProductById,
} from '../../services/sellerProductService';
import { getImageUrl } from '../../utils/image';

function ProductForm({ seller, product, groups, onClose, onSuccess }) {
  const isEdit = !!product;
  const [title, setTitle] = useState(product?.title || '');
  const [description, setDescription] = useState(product?.description || '');
  const [category, setCategory] = useState(product?.category || 'одежда');
  const [subcategory, setSubcategory] = useState(product?.subcategory || '');
  const [sellerSku, setSellerSku] = useState(product?.sellerSku || '');
  const [brand, setBrand] = useState(product?.brand || '');
  const [color, setColor] = useState(product?.color || '');
  const [selectedGroupId, setSelectedGroupId] = useState(
    product?.groupId || ''
  );

  const [sizes, setSizes] = useState(
    product?.variants?.map((v) => ({
      id: v.id,
      size: v.size,
      barcode: v.barcode || '',
      price: v.price || '',
      stock: v.stock || 0,
      weight_grams: v.weightGrams || '',
      length_cm: v.lengthCm || '',
      width_cm: v.widthCm || '',
      height_cm: v.heightCm || '',
    })) || [
      {
        size: '',
        barcode: '',
        price: '',
        stock: 0,
        weight_grams: '',
        length_cm: '',
        width_cm: '',
        height_cm: '',
      },
    ]
  );

  const [imageFiles, setImageFiles] = useState([]);

  // Синхронизация фото при изменении товара
  useEffect(() => {
    let urls = [];
    if (Array.isArray(product?.imageUrls)) {
      urls = product.imageUrls;
    } else if (typeof product?.imageUrls === 'string') {
      try {
        urls = JSON.parse(product.imageUrls);
      } catch {
        urls = [];
      }
    }
    if (!Array.isArray(urls)) urls = [];
    if (!urls.length && (product?.imageUrl || product?.image_url)) urls = [product.imageUrl || product.image_url];
    setImageFiles(
      urls.map((url, idx) => ({
        id: `existing-${idx}`,
        preview: getImageUrl(url),
        file: null,
        url,
      }))
    );
  }, [product]);

  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);

  const addSize = () =>
    setSizes([
      ...sizes,
      {
        size: '',
        barcode: '',
        price: '',
        stock: 0,
        weight_grams: '',
        length_cm: '',
        width_cm: '',
        height_cm: '',
      },
    ]);
  const removeSize = (index) => setSizes(sizes.filter((_, i) => i !== index));
  const updateSize = (index, field, value) => {
    const updated = [...sizes];
    updated[index] = { ...updated[index], [field]: value };
    setSizes(updated);
  };

  const handleCategoryChange = (newCategory) => {
    setCategory(newCategory);
    setSubcategory('');
  };

  const handleGroupChange = (groupId) => {
    setSelectedGroupId(groupId);
    if (groupId && !subcategory) {
      const group = groups.find((g) => g.id === parseInt(groupId));
      if (group?.subcategory) {
        setSubcategory(group.subcategory);
      }
    }
  };

  const isSubcategoryValid = subcategory && subcategory.trim() !== '';
  const subcategoryOptions = SUBCATEGORIES[category] || [];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title || !sellerSku.trim()) {
      alert('Название и артикул обязательны');
      return;
    }
    if (!isSubcategoryValid) {
      alert('Выберите подкатегорию');
      return;
    }

    const validSizes = sizes.filter((s) => s.size.trim() !== '');
    if (
      (category === 'одежда' || category === 'обувь') &&
      validSizes.length === 0
    ) {
      alert('Для одежды и обуви необходимо указать хотя бы один размер');
      return;
    }

    const variants = validSizes.map((s) => ({
      id: s.id || undefined,
      size: s.size.trim(),
      barcode: s.barcode || '',
      price: s.price !== '' && s.price !== null ? Number(s.price) : null,
      stock: isEdit ? s.stock || 0 : 0,
      weight_grams: s.weight_grams === '' ? null : Number(s.weight_grams),
      length_cm: s.length_cm === '' ? null : Number(s.length_cm),
      width_cm: s.width_cm === '' ? null : Number(s.width_cm),
      height_cm: s.height_cm === '' ? null : Number(s.height_cm),
    }));

    const formData = new FormData();
    formData.append('seller_id', seller.id);
    formData.append('title', title);
    formData.append('description', description);
    formData.append('category', category);
    formData.append('subcategory', subcategory.trim());
    formData.append('seller_sku', sellerSku.trim());
    formData.append('brand', brand.trim());
    formData.append('color', color.trim());
    if (selectedGroupId) formData.append('group_id', selectedGroupId);
    formData.append('variants', JSON.stringify(variants));

    // Всегда отправляем существующие URL, чтобы сервер не удалил старые фото
    const existingUrls = imageFiles
      .filter((img) => !img.file && img.url)
      .map((img) => img.url);
    // даже если пусто – отправляем пустой массив, чтобы сервер знал, что мы не хотим удалять
    formData.append('existing_image_urls', JSON.stringify(existingUrls));

    imageFiles.forEach((img) => {
      if (img.file) formData.append('images', img.file);
    });

    if (saving) return;
    setSaving(true);
    setMessage('');
    try {
      if (isEdit) {
        await updateProductById(product.id, formData, seller.id);
        alert('Товар обновлён');
      } else {
        await createProduct(formData);
        setMessage('Товар добавлен!');
      }
      onSuccess();
    } catch (err) {
      setMessage('Ошибка: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="seller-product-form"
      style={{
        background: 'white',
        borderRadius: 16,
        padding: 30,
        boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
        maxWidth: 700,
        margin: '0 auto',
      }}
    >
      <h2 style={{ marginBottom: 24 }}>
        {isEdit ? 'Редактирование товара' : 'Новый товар'}
      </h2>
      <form onSubmit={handleSubmit}>
        <label className="seller-form-field">
          Название товара
          <input
            placeholder="Название"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            style={{
              width: '100%',
              padding: 12,
              marginBottom: 12,
              borderRadius: 8,
              border: '1px solid #e2e8f0',
            }}
          />
        </label>
        <label className="seller-form-field">
          Описание
          <textarea
            placeholder="Описание"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            style={{
              width: '100%',
              padding: 12,
              marginBottom: 12,
              borderRadius: 8,
              border: '1px solid #e2e8f0',
            }}
          />
        </label>
        <div style={{ marginBottom: 12 }}>
          <label htmlFor="seller-product-category" style={{ marginRight: 10 }}>
            Категория:
          </label>
          <select
            id="seller-product-category"
            value={category}
            onChange={(e) => handleCategoryChange(e.target.value)}
            style={{ padding: 8, borderRadius: 6, border: '1px solid #e2e8f0' }}
          >
            <option value="одежда">Одежда</option>
            <option value="обувь">Обувь</option>
            <option value="аксессуары">Аксессуары</option>
          </select>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label
            htmlFor="seller-product-subcategory"
            style={{ marginRight: 10 }}
          >
            Подкатегория *:
          </label>
          <input
            list="subcategory-list"
            id="seller-product-subcategory"
            required
            value={subcategory}
            onChange={(e) => setSubcategory(e.target.value)}
            placeholder="Начните вводить или выберите"
            style={{
              padding: 8,
              borderRadius: 6,
              border: '1px solid #e2e8f0',
              width: '100%',
              maxWidth: 400,
            }}
          />
          <datalist id="subcategory-list">
            {subcategoryOptions.map((sc) => (
              <option key={sc} value={sc} />
            ))}
          </datalist>
        </div>
        <label className="seller-form-field">
          Артикул продавца
          <input
            placeholder="Артикул продавца (обязательно)"
            value={sellerSku}
            onChange={(e) => setSellerSku(e.target.value)}
            required
            style={{
              width: '100%',
              padding: 12,
              marginBottom: 12,
              borderRadius: 8,
              border: '1px solid #e2e8f0',
            }}
          />
        </label>
        <label className="seller-form-field">
          Бренд
          <input
            placeholder="Бренд"
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
            style={{
              width: '100%',
              padding: 12,
              marginBottom: 12,
              borderRadius: 8,
              border: '1px solid #e2e8f0',
            }}
          />
        </label>
        <label className="seller-form-field">
          Цвет
          <input
            placeholder="Цвет"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            style={{
              width: '100%',
              padding: 12,
              marginBottom: 12,
              borderRadius: 8,
              border: '1px solid #e2e8f0',
            }}
          />
        </label>
        <div style={{ marginBottom: 12 }}>
          <label htmlFor="seller-product-group" style={{ marginRight: 10 }}>
            Группа (склейка):
          </label>
          <select
            id="seller-product-group"
            value={selectedGroupId}
            onChange={(e) => handleGroupChange(e.target.value)}
            style={{ padding: 8, borderRadius: 6, border: '1px solid #e2e8f0' }}
          >
            <option value="">Без группы</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        </div>

        <SizeEditor
          sizes={sizes}
          onAdd={addSize}
          onRemove={removeSize}
          onUpdate={updateSize}
        />

        <div style={{ marginBottom: 20 }}>
          <label style={{ fontWeight: 600, display: 'block', marginBottom: 8 }}>
            Фотографии (до 10)
          </label>
          <ImageUploader images={imageFiles} onImagesChange={setImageFiles} />
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving
              ? 'Сохраняем…'
              : isEdit
                ? 'Сохранить изменения'
                : 'Добавить товар'}
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={onClose}
            className="btn btn-outline"
          >
            Отмена
          </button>
        </div>
      </form>
      {message && (
        <p
          className={`seller-notice seller-notice--${message.startsWith('Ошибка') ? 'error' : 'success'}`}
          role={message.startsWith('Ошибка') ? 'alert' : 'status'}
        >
          {message}
        </p>
      )}
    </div>
  );
}

export default ProductForm;
